import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Brain, Heart, TrendingUp, AlertTriangle, Users, Lightbulb,
  Activity, ChevronRight, RefreshCw, ArrowLeft, Target, Sparkles,
  CheckCircle, Clock, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Component can be used by both Parent and Principal
const PredictiveInsights = ({ userType = 'parent' }) => {
  const navigate = useNavigate();
  const { childId } = useParams();
  
  const [insights, setInsights] = useState(null);
  const [allChildrenInsights, setAllChildrenInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedChild, setSelectedChild] = useState(childId || null);
  const [children, setChildren] = useState([]);

  const getToken = () => {
    return userType === 'principal' 
      ? localStorage.getItem('principal_token')
      : localStorage.getItem('parent_token');
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate(userType === 'principal' ? '/principal/login' : '/parent/login');
      return;
    }
    loadData();
  }, [navigate, selectedChild, userType]);

  const loadData = async () => {
    try {
      const token = getToken();
      
      if (userType === 'principal') {
        // Principal view - get overview
        const overviewRes = await axios.get(
          `${BACKEND_URL}/api/predictive/principal/overview?token=${token}`
        );
        setAllChildrenInsights(overviewRes.data);
        
        // If specific child selected, get their insights
        if (selectedChild) {
          const childRes = await axios.get(
            `${BACKEND_URL}/api/predictive/child/${selectedChild}?token=${token}`
          );
          setInsights(childRes.data);
        }
      } else {
        // Parent view
        const allRes = await axios.get(
          `${BACKEND_URL}/api/predictive/parent/all?token=${token}`
        );
        setChildren(allRes.data.children || []);
        
        // If no child selected, select first
        if (!selectedChild && allRes.data.children?.length > 0) {
          setSelectedChild(allRes.data.children[0].child_id);
          return;
        }
        
        if (selectedChild) {
          const childRes = await axios.get(
            `${BACKEND_URL}/api/predictive/child/${selectedChild}?token=${token}`
          );
          setInsights(childRes.data);
        }
      }
    } catch (error) {
      console.error('Error loading insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshInsights = async () => {
    if (!selectedChild) return;
    
    try {
      setRefreshing(true);
      const token = getToken();
      const response = await axios.get(
        `${BACKEND_URL}/api/predictive/child/${selectedChild}?token=${token}&refresh=true`
      );
      setInsights(response.data);
    } catch (error) {
      console.error('Error refreshing insights:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      mood_prediction: Heart,
      development_milestone: TrendingUp,
      behavioral_pattern: Activity,
      social_dynamics: Users,
      risk_indicator: AlertTriangle,
      recommendation: Lightbulb
    };
    return icons[category] || Brain;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };
    return styles[priority] || styles.low;
  };

  const NavComponent = userType === 'principal' 
    ? require('../components/PrincipalNav').default 
    : require('../components/ParentNav').default;

  const isParent = userType === 'parent';
  const accentSpinner = isParent ? 'border-orange-500' : 'border-purple-600';
  const pageBg = isParent
    ? 'bg-gradient-to-br from-orange-50 via-white to-rose-50'
    : 'bg-gradient-to-br from-purple-50 to-indigo-100';
  const headingIcon = isParent ? 'text-orange-500' : 'text-purple-600';
  const activeChildBtn = isParent ? 'bg-orange-500 hover:bg-orange-600' : 'bg-purple-600';
  const childAvatarBg = isParent ? 'bg-orange-100' : 'bg-purple-100';
  const childAvatarIcon = isParent ? 'text-orange-500' : 'text-purple-600';

  if (loading) {
    return (
      <div className={`min-h-screen ${pageBg}`}>
        <NavComponent />
        <div className="pt-20 flex items-center justify-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${accentSpinner}`}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${pageBg}`} data-testid="predictive-insights-page">
      <NavComponent />

      <div className="pt-20 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate(userType === 'principal' ? '/principal/dashboard' : '/parent/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Brain className={`w-6 h-6 ${headingIcon}`} />
                  AI Insights
                </h1>
                <p className="text-gray-600 text-sm">AI-powered behavioral analysis and predictions</p>
              </div>
            </div>
          </div>

          {/* Principal Overview (if principal view) */}
          {userType === 'principal' && allChildrenInsights && (
            <Card className="mb-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-4xl font-bold">{allChildrenInsights.total_children_analyzed}</p>
                    <p className="text-purple-100 text-sm">Children Analyzed</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold">{allChildrenInsights.total_insights_generated}</p>
                    <p className="text-purple-100 text-sm">Total Insights</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl font-bold text-red-300">{allChildrenInsights.children_needing_attention?.length || 0}</p>
                    <p className="text-purple-100 text-sm">Need Attention</p>
                  </div>
                  <div className="text-center">
                    <Sparkles className="w-8 h-8 mx-auto text-yellow-300 mb-1" />
                    <p className="text-purple-100 text-sm">AI Powered</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Children Needing Attention (Principal view) */}
          {userType === 'principal' && allChildrenInsights?.children_needing_attention?.length > 0 && (
            <Card className="mb-6 border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  Children Needing Attention
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {allChildrenInsights.children_needing_attention.map((child, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100"
                      onClick={() => setSelectedChild(child.child_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{child.child_name}</p>
                          <p className="text-sm text-red-600">{child.top_concern}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-1 bg-red-200 text-red-800 rounded-full text-xs font-medium">
                          {child.high_priority_count} high priority
                        </span>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Child Selector (Parent view with multiple children) */}
          {userType === 'parent' && children.length > 1 && (
            <Card className="mb-6">
              <CardContent className="p-4">
                <label className="text-sm text-gray-600 mb-2 block">Select Child</label>
                <div className="flex gap-2 flex-wrap">
                  {children.map((child) => (
                    <Button
                      key={child.child_id}
                      variant={selectedChild === child.child_id ? 'default' : 'outline'}
                      onClick={() => setSelectedChild(child.child_id)}
                      className={selectedChild === child.child_id ? activeChildBtn : ''}
                    >
                      {child.child_name}
                      {child.high_priority_count > 0 && (
                        <span className="ml-2 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                          {child.high_priority_count}
                        </span>
                      )}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Selected Child Insights */}
          {insights && (
            <>
              {/* Child Header */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 ${childAvatarBg} rounded-full flex items-center justify-center`}>
                        <Brain className={`w-8 h-8 ${childAvatarIcon}`} />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">{insights.child_name}&apos;s Insights</h2>
                        <p className="text-gray-500">Age {insights.child_age} • Last updated: {new Date(insights.generated_at).toLocaleString()}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={refreshInsights}
                      disabled={refreshing}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                  
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className={`p-4 rounded-lg ${insights.summary?.high_priority > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                      <p className={`text-2xl font-bold ${insights.summary?.high_priority > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                        {insights.summary?.high_priority || 0}
                      </p>
                      <p className="text-sm text-gray-500">High Priority</p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <p className="text-2xl font-bold text-yellow-600">{insights.summary?.medium_priority || 0}</p>
                      <p className="text-sm text-gray-500">Medium Priority</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{insights.summary?.total_insights || 0}</p>
                      <p className="text-sm text-gray-500">Total Insights</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Insights List */}
              {insights.insights?.length > 0 ? (
                <div className="space-y-4">
                  {insights.insights.map((insight, idx) => {
                    const CategoryIcon = getCategoryIcon(insight.category);
                    const categoryInfo = insight.category_info || {};
                    
                    return (
                      <Card key={insight.id || idx} className="overflow-hidden">
                        <div 
                          className="h-1"
                          style={{ backgroundColor: categoryInfo.color || '#6b7280' }}
                        />
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div 
                              className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: `${categoryInfo.color}20` || '#f3f4f6' }}
                            >
                              <CategoryIcon 
                                className="w-6 h-6" 
                                style={{ color: categoryInfo.color || '#6b7280' }}
                              />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-bold text-gray-900">{insight.title}</h3>
                                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityBadge(insight.priority)}`}>
                                    {insight.priority?.toUpperCase()}
                                  </span>
                                </div>
                                {insight.confidence && (
                                  <span className="text-sm text-gray-400">
                                    {insight.confidence}% confidence
                                  </span>
                                )}
                              </div>
                              
                              <p className="text-gray-600 mb-3">{insight.summary}</p>
                              
                              {/* Recommendation */}
                              {insight.recommendation && (
                                <div className="flex items-start gap-2 p-3 bg-white border border-gray-200 rounded-lg">
                                  <Lightbulb className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-800">Recommendation</p>
                                    <p className="text-sm text-gray-600">{insight.recommendation}</p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Data Details (collapsed) */}
                              {insight.data && Object.keys(insight.data).length > 0 && (
                                <details className="mt-3">
                                  <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700">
                                    View details
                                  </summary>
                                  <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                                    <pre className="text-xs text-gray-600 overflow-x-auto">
                                      {JSON.stringify(insight.data, null, 2)}
                                    </pre>
                                  </div>
                                </details>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Brain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No Insights Available</h3>
                    <p className="text-gray-500 mb-4">
                      Not enough data to generate predictive insights yet.
                      <br />
                      Continue with regular sessions to unlock AI predictions.
                    </p>
                    <Button onClick={refreshInsights} disabled={refreshing}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                      Try Generating
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Category Legend */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-sm font-medium text-gray-700">Insight Categories</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { id: 'mood_prediction', name: 'Mood Forecast', icon: Heart, color: '#ec4899' },
                  { id: 'development_milestone', name: 'Development', icon: TrendingUp, color: '#10b981' },
                  { id: 'behavioral_pattern', name: 'Behavior', icon: Activity, color: '#3b82f6' },
                  { id: 'social_dynamics', name: 'Social', icon: Users, color: '#8b5cf6' },
                  { id: 'risk_indicator', name: 'Risk Alert', icon: AlertTriangle, color: '#f59e0b' },
                  { id: 'recommendation', name: 'Suggestion', icon: Lightbulb, color: '#06b6d4' }
                ].map((cat) => {
                  const Icon = cat.icon;
                  return (
                    <div key={cat.id} className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${cat.color}20` }}
                      >
                        <Icon className="w-4 h-4" style={{ color: cat.color }} />
                      </div>
                      <span className="text-sm text-gray-700">{cat.name}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Export both parent and principal versions
export const ParentPredictiveInsights = () => <PredictiveInsights userType="parent" />;
export const PrincipalPredictiveInsights = () => <PredictiveInsights userType="principal" />;
export default ParentPredictiveInsights;
